/*
  Runtime config loader.

  On app mount we hit GET /api/admin/public-config once and merge the result
  with the build-time process.env values. Anything set in the DB wins over
  the env var. The resolved config is published on window.__VH_CONFIG__ so
  non-React consumers (adsense.js, ethicalads.js, analytics.js) can read it.

  Order of resolution for any key:
    1. DB override (via /api/admin/public-config)
    2. process.env.REACT_APP_* (baked at build time)
    3. empty string
*/

import { API } from "./api";

const DEFAULT = {
  adsense_client_id: "",
  adsense_slot_in_feed: "",
  adsense_slot_desktop_inline: "",
  adsense_slot_sidebar: "",
  ethicalads_id: "",
  ga_id: "",
  gtm_id: "",
  custom_ads: { in_feed: "", desktop_inline: "", sidebar: "" },
  logo_override: false,
  feature_flags: {},
  dinner_plate_max_menu_files: 3,
};

const PREVIEW_KEY = "vh_preview_config";
const PREVIEW_TTL_MS = 10 * 60 * 1000; // 10 min
export const PREVIEW_QUERY_PARAM = "preview";

function readPreview() {
  try {
    const url = new URL(window.location.href);
    const nonce = url.searchParams.get(PREVIEW_QUERY_PARAM);
    if (!nonce) return null;
    const raw = localStorage.getItem(PREVIEW_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.nonce !== nonce) return null;
    if (!parsed.created_at || Date.now() - parsed.created_at > PREVIEW_TTL_MS) return null;
    return parsed.data || null;
  } catch { return null; }
}

export function isPreviewMode() {
  return !!readPreview();
}

export function exitPreview() {
  try { localStorage.removeItem(PREVIEW_KEY); } catch { /* noop */ }
  const url = new URL(window.location.href);
  url.searchParams.delete(PREVIEW_QUERY_PARAM);
  window.location.href = url.pathname + (url.search ? `?${url.searchParams.toString()}` : "");
}

/* Admin-side helper — write a preview payload and return the target URL. */
export function writePreview(formValues) {
  const nonce = (crypto && crypto.randomUUID) ? crypto.randomUUID() : `p-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  const payload = { nonce, created_at: Date.now(), data: formValues };
  try { localStorage.setItem(PREVIEW_KEY, JSON.stringify(payload)); } catch { /* noop */ }
  return `/?${PREVIEW_QUERY_PARAM}=${encodeURIComponent(nonce)}`;
}

function envConfig() {
  return {
    adsense_client_id: process.env.REACT_APP_ADSENSE_CLIENT_ID || "",
    adsense_slot_in_feed: process.env.REACT_APP_ADSENSE_SLOT_IN_FEED || "",
    adsense_slot_desktop_inline: process.env.REACT_APP_ADSENSE_SLOT_DESKTOP_INLINE || "",
    adsense_slot_sidebar: process.env.REACT_APP_ADSENSE_SLOT_SIDEBAR || "",
    ethicalads_id: process.env.REACT_APP_ETHICALADS_ID || "",
    ga_id: process.env.REACT_APP_GA_ID || "",
    gtm_id: process.env.REACT_APP_GTM_ID || "",
    custom_ads: { in_feed: "", desktop_inline: "", sidebar: "" },
    logo_override: false,
  };
}

export function getConfig() {
  return window.__VH_CONFIG__ || envConfig();
}

export async function loadRuntimeConfig() {
  // Seed with env defaults immediately so synchronous readers get something.
  window.__VH_CONFIG__ = envConfig();
  try {
    const resp = await fetch(`${API}/admin/public-config`, { credentials: "omit" });
    if (!resp.ok) return window.__VH_CONFIG__;
    const db = await resp.json();
    const env = envConfig();
    // DB wins wherever it has a truthy value; otherwise env is used.
    const merged = { ...DEFAULT, ...env };
    for (const k of Object.keys(db)) {
      const v = db[k];
      if (k === "custom_ads") {
        merged.custom_ads = { ...merged.custom_ads, ...(v || {}) };
      } else if (k === "feature_flags" && typeof v === "object" && v !== null) {
        // Merge feature_flags object from DB
        merged.feature_flags = { ...(merged.feature_flags || {}), ...v };
      } else if (typeof v === "string" && v.trim().length > 0) {
        merged[k] = v.trim();
      } else if (typeof v === "boolean") {
        merged[k] = v;
      } else if (typeof v === "number" && Number.isFinite(v)) {
        merged[k] = v;
      }
    }
    // Preview mode — admin form values beat both env and DB
    const preview = readPreview();
    if (preview) {
      for (const k of Object.keys(preview)) {
        if (k.startsWith("custom_ad_")) {
          const slot = k.slice("custom_ad_".length);
          merged.custom_ads = { ...merged.custom_ads, [slot]: preview[k] || "" };
        } else if (typeof preview[k] === "string") {
          merged[k] = preview[k];
        }
      }
    }
    window.__VH_CONFIG__ = merged;
    window.dispatchEvent(new Event("vh-config-loaded"));
    return merged;
  } catch {
    return window.__VH_CONFIG__;
  }
}
